import StationScreen from './StationScreen';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import "./flashStyle.css";
import "./Kitchen.css";
import { toast } from "react-toastify";
import config from '../config';
import { getPollMs } from '../utils/appSettings';
import { useBpFlags } from "../Responsive";
import TableSelector from './TableSelector';
import AssignMeats from './AssignMeats';
import { bbqIcon } from '../utils/bbqTerms';

import i18n from "../i18n";
const tr = i18n.t.bind(i18n);

const stateLabel = (stateName) => {
  const s = String(stateName || '').toLowerCase();

  const map = {
    sitting: tr('Starters'),
    preparing: tr('Appetizers'),
    ontheside: tr('Siders'),
    dessert: tr('Desserts'),
  };

  return map[s] ?? stateName;
};

const ALARMS_KEY = "estiasync_alarms_on";
const VOICEOVER_KEY = "estiasync_voiceover";

const KitchenView = () => {

  const [reservations, setReservations] = useState([]);
  const [newRecords, setNewRecords] = useState([]);
  const [details, setDetails] = useState({});
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [audio] = useState(new Audio('/alarm.wav'));
  const [playing, setPlaying] = useState(false);
  const [alarmsMuted, setAlarmsMuted] = useState(
    () => localStorage.getItem(ALARMS_KEY) === "1"
  );
  
  // if alarms were ON previously, treat as "used" so notifications can play immediately
  const [alarmUsed, setAlarmUsed] = useState(
    () => localStorage.getItem(ALARMS_KEY) === "1"
  );
  const [notify, setNotify] = useState(false);
  const [pendingActions, setPendingActions] = useState({});
  const [sentActions, setSentActions] = useState({});
  const { isTablet } = useBpFlags();
  const [showSidersSelector, setShowSidersSelector] = useState(false);
  const [activeReservationIds, setActiveReservationIds] = useState([]);
  const [selectorTime, setSelectorTime] = useState(
    () => new Date().toISOString().slice(0, 16)   // YYYY-MM-DDTHH:MM
  );
  const [assignMeatsReservation, setAssignMeatsReservation] = useState(null);
  
  // ---- toast de-duplication (like BBQ) ----
  const STORAGE_KITCHEN_SEEN_NEW = "estiasync_kitchen_seen_new";
  const STORAGE_KITCHEN_SEEN_OVERDUE = "estiasync_kitchen_seen_overdue";
  
  const loadSet = (key) => {
    try {
      const arr = JSON.parse(sessionStorage.getItem(key) || "[]");
      return new Set((arr || []).map(String));
    } catch {
      return new Set();
    }
  };
  
  const saveSet = (key, set) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(Array.from(set)));
    } catch {}
  };
  
  const seenNewRef = useRef(loadSet(STORAGE_KITCHEN_SEEN_NEW));         // "New ..." toasts
  const seenOverdueRef = useRef(loadSet(STORAGE_KITCHEN_SEEN_OVERDUE)); // "Finish ..." toasts

  useEffect(() => {
    return () => {
      saveSet(STORAGE_KITCHEN_SEEN_NEW, seenNewRef.current);
      saveSet(STORAGE_KITCHEN_SEEN_OVERDUE, seenOverdueRef.current);
    };
  }, []);

  const stateDurations = {
      sitting: 2*60, // minutes
      preparing: 12*60, // minutes
      ontheside: 10*60, // minutes
      dessert: 3*60, // minutes
  };

  const toggle = () => {
    const nextOn = !alarmsMuted;
  
    setAlarmUsed(true);
    setAlarmsMuted(nextOn);
    localStorage.setItem(ALARMS_KEY, nextOn ? "1" : "0");
  
    if (nextOn) setPlaying(true);
    else setPlaying(false);
  };

  // --- VoiceOver (English-only) ---
  const [voiceOverEnabled, setVoiceOverEnabled] = useState(
    () => localStorage.getItem(VOICEOVER_KEY) === "1"
  );

  const voiceOverEnabledRef = useRef(voiceOverEnabled);
  useEffect(() => { voiceOverEnabledRef.current = voiceOverEnabled; }, [voiceOverEnabled]);

  const speechQueueRef = useRef([]);
  const speechSpeakingRef = useRef(false);

  const clearSpeechQueue = () => {
    speechQueueRef.current = [];
    speechSpeakingRef.current = false;
    try {
      const synth = window.speechSynthesis;
      if (synth) synth.cancel();
    } catch {}
  };

  const extractToastText = (content) => {
    if (content == null) return "";
    if (typeof content === "function") return extractToastText(content());
    if (typeof content === "string" || typeof content === "number") return String(content);
    if (Array.isArray(content)) return content.map(extractToastText).join(" ");
    if (React.isValidElement(content)) return extractToastText(content.props?.children);
    return "";
  };

  const toKitchenSpeechText = (raw) => {
    let s = String(raw || "").trim();
    if (!s) return "";
  
    // If the toast follows the pattern "12) Something: 12345" → drop ": 12345"
    if (/\)\s*.*:\s*\d+\s*$/.test(s)) {
      s = s.replace(/:\s*\d+\s*$/, "").trim();
    }
  
    // Turn "12) ..." into "12 ..." (remove the ")")
    s = s.replace(/^\s*(\d+)\)\s*/, "$1 ").trim();
  
    return s;
  };

  const pickEnglishVoice = (synth) => {
    try {
      const voices = typeof synth.getVoices === "function" ? synth.getVoices() : [];
      return voices.find(v => String(v.lang || "").toLowerCase().startsWith("en")) || null;
    } catch {
      return null;
    }
  };

  const speakNextFromQueue = () => {
    if (!voiceOverEnabledRef.current) return;
    if (speechSpeakingRef.current) return;

    const synth = window.speechSynthesis;
    if (!synth || !window.SpeechSynthesisUtterance) return;

    const next = speechQueueRef.current.shift();
    if (!next) return;

    speechSpeakingRef.current = true;

    const u = new SpeechSynthesisUtterance(next.text);
    u.lang = "en-US"; // ALWAYS English

    const v = pickEnglishVoice(synth);
    if (v) u.voice = v;

    u.onend = () => {
      speechSpeakingRef.current = false;
      speakNextFromQueue();
    };

    u.onerror = () => {
      speechSpeakingRef.current = false;
      speakNextFromQueue();
    };

    try { synth.resume?.(); } catch {}
    synth.speak(u);
  };

  const enqueueSpeech = (text) => {
    const msg = String(text || "").trim();
    if (!msg) return;
    speechQueueRef.current.push({ text: msg });
    speakNextFromQueue();
  };

  const toggleVoiceOver = () => {
    const next = !voiceOverEnabled;

    if (!next) clearSpeechQueue();

    // Don’t enable if unsupported
    if (next && (!window.speechSynthesis || !window.SpeechSynthesisUtterance)) {
      toast.info("VoiceOver is not supported in this browser.");
      return;
    }

    voiceOverEnabledRef.current = next;

    setVoiceOverEnabled(next);
    localStorage.setItem(VOICEOVER_KEY, next ? "1" : "0");

    if (next) {
      // speak immediately on enable
      enqueueSpeech("VoiceOver enabled");
    }
  };

  const toastOrigRef = useRef(null);
  const toastWrappedRef = useRef(false);

  useEffect(() => {
    if (!voiceOverEnabled) {
      if (toastWrappedRef.current && toastOrigRef.current) {
        const o = toastOrigRef.current;
        toast.success = o.success;
        toast.error   = o.error;
        toast.warn    = o.warn;
        toast.info    = o.info;
        if (o.dark) toast.dark = o.dark;
        toastWrappedRef.current = false;
        clearSpeechQueue();
      }
      return;
    }

    if (toastWrappedRef.current) return;

    toastOrigRef.current = {
      success: toast.success,
      error: toast.error,
      warn: toast.warn,
      info: toast.info,
      dark: toast.dark,
    };

    const wrap = (fn) => (content, opts) => {
      const id = fn(content, opts);
      const raw = extractToastText(content);
      const spoken = toKitchenSpeechText(raw);
      enqueueSpeech(spoken);
      return id;
    };

    toast.success = wrap(toastOrigRef.current.success);
    toast.error   = wrap(toastOrigRef.current.error);
    toast.warn    = wrap(toastOrigRef.current.warn);
    toast.info    = wrap(toastOrigRef.current.info);
    if (toastOrigRef.current.dark) toast.dark = wrap(toastOrigRef.current.dark);

    toastWrappedRef.current = true;

    return () => {
      if (toastWrappedRef.current && toastOrigRef.current) {
        const o = toastOrigRef.current;
        toast.success = o.success;
        toast.error   = o.error;
        toast.warn    = o.warn;
        toast.info    = o.info;
        if (o.dark) toast.dark = o.dark;
        toastWrappedRef.current = false;
      }
    };
  }, [voiceOverEnabled]);


  useEffect(() => {
      playing ? audio.play() : audio.pause();
    },
    [playing]
  );

  useEffect(() => {
    audio.addEventListener('ended', () => setPlaying(false));
    return () => {
      audio.removeEventListener('ended', () => setPlaying(false));
    };
  }, []);

  useEffect(() => {
    if(reservations && !reservations.length)
      setMessage('');
  }, [reservations]);

  // Open AssignMeats modal for a reservation (from any place: table selector OR row icon)
  const openAssignMeatsForReservationId = (reservationId) => {
    const id = Number(reservationId);
    if (!id) return;
  
    // If you ever add richer reservation info in details, this will pick it up.
    const reservation = details[id]?.reservation || { id };
  
    setAssignMeatsReservation(reservation);
  };


  const fetchReservations = async () => {
    try {
        const response = await axios.get(tr('{{API_BASE_URL}}/assignedstates/kitchen-view', {
          API_BASE_URL: config.API_BASE_URL
        }));
        const newReservations = response.data;
        var details = [];
        var promises = [];
        var notify = false;    

        newReservations.forEach(r => {
          const idStr = String(r.assignedstate_id);
          const stateKey = String(r.state_name || "").toLowerCase();
          const limitSec = stateDurations[stateKey];
        
          // 1) Overdue "Finish ..." toast ONCE per assignedstate_id (even across tab changes)
          if (
            limitSec != null &&
            r.seconds_passed >= limitSec &&
            !seenOverdueRef.current.has(idStr)
          ) {
            seenOverdueRef.current.add(idStr);
            notify = true;
        
            toast.error(tr('{{var1}}) Finish {{state_name}}: {{reservation_id}}', {
              var1: ('  ' + r.assignedstate_id).slice(-2),
              state_name: stateLabel(r.state_name),
              reservation_id: r.reservation_id
            }));
          }
        
          // 2) Details fetch (unchanged)
          promises.push(
            axios
              .get(tr('{{API_BASE_URL}}/reservation-details/{{reservation_id}}', {
                API_BASE_URL: config.API_BASE_URL,
                reservation_id: r.reservation_id
              }))
              .then((response) => {
                details[r.reservation_id] = response.data;
              })
              .catch((error) => {
                console.error(
                  tr('Failed to retrieve details for reservation {{reservation_id}}:', {
                    reservation_id: r.reservation_id
                  }),
                  error.message
                );
              })
          );
        });


        // Fetch assigned states that have been completed but their corresponding action is not yet completed
        const assignedStatesResponse = await axios.get(tr('{{API_BASE_URL}}/assignedstates/kitchen-view/pending-actions', {
          API_BASE_URL: config.API_BASE_URL
        }));
        
        if (assignedStatesResponse.data.length > 0) {
            let sent = {};
            assignedStatesResponse.data.forEach(item => {
                sent[item.assignedstate_id] = true;
            });
            //console.log('sent',sent)
            setSentActions(sent);
        }

        Promise.all(promises).then(() => {
            setDetails(details);
        });

        setNotify(notify);

        // Detect truly new items using a persistent "seen" set (survives tab changes)
        const actuallyNew = [];
        for (const r of newReservations) {
          const idStr = String(r.assignedstate_id);
          if (!seenNewRef.current.has(idStr)) {
            seenNewRef.current.add(idStr);
            actuallyNew.push(r);
          }
        }
        
        if (actuallyNew.length) {
          setNewRecords(actuallyNew); // keep your existing effect (below) if you want
        }
        
        setReservations(newReservations);
        
    } catch (err) {
        console.error('Error fetching kitchen view:', err);
        setError('Failed to fetch kitchen view data');
    }
  };

  useEffect(() => {
      if (alarmUsed && alarmsMuted && notify) 
        setPlaying(true);
  }, [notify]);

  useEffect(() => {
    if (!newRecords.length) return;
  
    newRecords.forEach(r => {
      toast.warn(tr('{{var1}}) New {{state_name}}: {{reservation_id}}', {
        var1: ('  ' + r.assignedstate_id).slice(-2),
        state_name: stateLabel(r.state_name),
        reservation_id: r.reservation_id
      }));
    });
  
    if (alarmUsed && alarmsMuted) setPlaying(true);
  
    // important: clear so it never replays from the same array instance
    setNewRecords([]);
  }, [newRecords, alarmUsed, alarmsMuted]);

  useEffect(() => {
      fetchReservations();

      // Set up interval to fetch data every 1 second
      const interval = setInterval(fetchReservations, getPollMs('fast'));

      return () => clearInterval(interval); // Cleanup interval on unmount
  }, []);

  const calculateBarWidth = (stateName, minutesPassed) => {
      const totalDuration = stateDurations[stateName.toLowerCase()];
      if (!totalDuration) return 0;

      const elapsedTime = Math.min(minutesPassed, totalDuration);
      return (elapsedTime / totalDuration) * 100;
  };

  const calculateBarColor = (stateName, minutesPassed) => {
      const totalDuration = stateDurations[stateName.toLowerCase()];
      if (!totalDuration) return 'gray';

      const elapsedTime = Math.min(minutesPassed, totalDuration);
      return elapsedTime >= totalDuration ? 'darkRed' : '#4caf50';
  };

  const handleCompleteAction = (assignedstate_id) => {
      if (pendingActions[assignedstate_id]) return;

      setPendingActions((prev) => ({ ...prev, [assignedstate_id]: true }));

      const timer = setTimeout(() => {
        axios.post(tr('{{API_BASE_URL}}/assignedstates/kitchen-view/ready', {
          API_BASE_URL: config.API_BASE_URL
        }), { assignedstate_id })
            .then(({ data }) => {
          if (data?.createdAction) {
            setSentActions(prev => ({ ...prev, [assignedstate_id]: true }));
            setMessage(tr('Service notified for state #{{assignedstate_id}}', {
              assignedstate_id
            }));
          } else {
            setMessage(tr('State #{{assignedstate_id}} is ready (no service action needed)', {
              assignedstate_id
            }));
          }
        })
            .catch((err) => {
          console.error('Failed to create a service action:', err);
          setError(tr('Failed to create a service action for state: {{var1}}', {
            var1: ('  '+assignedstate_id).slice(-2)
          }));
        })
            .finally(() => {
                setPendingActions((prev) => {
                    const updated = { ...prev };
                    delete updated[assignedstate_id];
                    return updated;
                });
            });
      }, 6000);

      setPendingActions((prev) => ({ ...prev, [assignedstate_id]: timer }));
  };

  const cancelCompleteAction = (assignedstate_id) => {
      if (pendingActions[assignedstate_id]) {
          clearTimeout(pendingActions[assignedstate_id]);

          setPendingActions((prev) => {
              const updated = { ...prev };
              delete updated[assignedstate_id];
              return updated;
          });
      }
  };

  const removeAction = (assignedstate_id) => {
    axios.post(tr('{{API_BASE_URL}}/assignedstates/kitchen-view/complete', {
      API_BASE_URL: config.API_BASE_URL
    }), { assignedstate_id })
        .then(() => {
      let sent = sentActions;
      delete sent[assignedstate_id];
      setSentActions(sent);
      setReservations((prev) =>
          prev.filter((item) => item.assignedstate_id !== assignedstate_id)
      );
      setMessage(tr('State {{var1}} marked as completed', {
        var1: ('  '+assignedstate_id).slice(-2)
      }));
    })
        .catch((err) => {
      console.error('Error marking state as completed:', err);
      setError(tr('Failed to mark state {{var1}} as completed', {
        var1: ('  '+assignedstate_id).slice(-2)
      }));
    })
  };

  // --- "Add Siders" flow ---

  // 1) Open TableSelector for active reservations (incomplete + has sitting)
  const openAddSiders = async () => {
    try {
      const { data } = await axios.get(
        tr('{{API_BASE_URL}}/assignedstates/active-sitting-reservations', {
          API_BASE_URL: config.API_BASE_URL
        })
      );

      const ids = Array.from(
        new Set(
          (data || [])
            .map(row => Number(row.reservation_id))
            .filter(Boolean)
        )
      );

      if (!ids.length) {
        toast.info('No active sitting reservations right now.');
        return;
      }

      setActiveReservationIds(ids);
      setSelectorTime(new Date().toISOString().slice(0, 16));
      setShowSidersSelector(true);
    } catch (err) {
      console.error('Error preparing Add Siders:', err);
      toast.error('Failed to load active reservations for siders.');
    }
  };

  const closeSidersSelector = () => {
    setShowSidersSelector(false);
  };

  // 2) When user clicks a table in the selector
  const handleTablePickedForSiders = (table) => {
    const reservationId = Number(table?.reserved_id);
    if (!reservationId) return;

    // Reuse reservation details if we already fetched them
    const reservation =
      details[reservationId]?.reservation || { id: reservationId };

    setAssignMeatsReservation(reservation);
    setShowSidersSelector(false);
  };

  // 3) Close AssignMeats modal
  const handleCloseAssignMeats = () => {
    setAssignMeatsReservation(null);
  };


  const headerRowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    margin: '18px 0 10px',
    flexWrap: 'wrap',
  };

  const headerLeftStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  };

  const headerTitleStyle = {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: '#111827',
  };

  const utilityButtonBaseStyle = {
    color: 'white',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: 10,
    padding: '6px 10px',
    minHeight: 34,
    minWidth: 126,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    fontWeight: 700,
    fontSize: 13,
    lineHeight: 1.1,
    boxShadow: '0 7px 14px rgba(0,0,0,0.10)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  const addSidersButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: '1px solid #0f4c81',
    background: 'linear-gradient(180deg, #1d71b8 0%, #0f4c81 100%)',
    color: 'white',
    borderRadius: 999,
    padding: '7px 14px',
    fontWeight: 700,
    fontSize: 13,
    letterSpacing: 0.2,
    boxShadow: '0 10px 22px rgba(15, 76, 129, 0.24)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  const topControlsStyle = {
    position: 'fixed',
    top: isTablet ? '22px' : '78px',
    right: '30px',
    display: 'flex',
    gap: 8,
    zIndex: 9999,
  };

  const alarmsButtonStyle = {
    ...utilityButtonBaseStyle,
    background: alarmsMuted ? 'darkGreen' : 'darkRed',
  };

  const voiceButtonStyle = {
    ...utilityButtonBaseStyle,
    background: voiceOverEnabled ? 'darkGreen' : 'darkRed',
  };

  if (error) {
      return <div className="error">{error}</div>;
  }

  return (
    <>
    <StationScreen station="kitchen" embedded />
    <div className={isTablet ? 'tablet-scroll' : ''} style={{ width: '95%', margin: 'auto' }}>
      <div style={headerRowStyle}>
        <div style={headerLeftStyle}>
          <h2 style={headerTitleStyle}>{tr('Kitchen View')}</h2>
          <button onClick={openAddSiders} style={addSidersButtonStyle}>
            <span aria-hidden="true">&#127869;</span>
            <span>{tr('Add Siders')}</span>
          </button>
        </div>
      </div>

      <div style={topControlsStyle}>
        <button style={alarmsButtonStyle} onClick={toggle}>
          <span>{tr('Alarms')}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {alarmsMuted ? (
              <>ON <span aria-hidden="true">&#x266B;</span></>
            ) : (
              <>OFF <span aria-hidden="true">&#x1F507;</span></>
            )}
          </span>
        </button>

        <button style={voiceButtonStyle} onClick={toggleVoiceOver}>
          <span>{tr('VoiceOver')}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {voiceOverEnabled ? (
              <>ON <span aria-hidden="true">&#x1F50A;</span></>
            ) : (
              <>OFF <span aria-hidden="true">&#x1F507;</span></>
            )}
          </span>
        </button>
      </div>
      {message && <span style={{ color: 'green' }}>{message}</span>}
      {reservations.length === 0 ? (
          <p>{tr('No more tasks found yet...')}</p>
      ) : (
          <div className="table-wrap">
            {/* Optional zoom on tablet:
               {isTablet ? <div className="scale-wrap"> : null} */}
            <table className={isTablet ? 'kitchenTable scale-tablet-95' : 'kitchenTable'}>
              <thead>
                  <tr>
                      <th>{tr('ID')}</th>
                      <th>{tr('Order')}</th>
                      <th>{tr('Reservation')}</th>
                      <th>{tr('People')}</th>
                      <th>{tr('Actions')}</th>
                      <th>{tr('Timer')}</th>
                      <th>{tr('Done')}</th>
                      <th>{tr('Siders')}</th>
                      <th>{tr('Tables')}</th>
                      <th>{tr('Client')}</th>
                  </tr>
              </thead>
              <tbody>
                  {reservations.map((item) => {
                    const barWidth = calculateBarWidth(item.state_name, item.seconds_passed);
                    const barColor = calculateBarColor(item.state_name, item.seconds_passed);

                    return (
                      <tr key={item.assignedstate_id}>
                        <td><b style={{color: 'darkBlue'}}>{('  '+item.assignedstate_id).slice(-2)}</b></td>
                        <td>{item.order || ''}</td>
                        <td>{item.reservation_id}</td>
                        <td>{item.people}</td>
                        <td
                            style={{
                                backgroundColor: item.state_color,
                                color: '#fff',
                                borderRadius: '8px',
                                border: '4px solid white'
                            }}
                        >
                            {stateLabel(item.state_name)}
                        </td>
                        <td>
                            {item.seconds_passed > 60 && (Math.floor(item.seconds_passed/60)+"':")}{('0'+(item.seconds_passed%60)).slice(-2)}"
                            <div
                                style={{
                                    width: '100%',
                                    backgroundColor: '#e0e0e0',
                                    height: '9px',
                                    marginTop: '5px',
                                    position: 'relative',
                                }}
                            >
                                <div
                                    style={{
                                        width: tr('{{barWidth}}%', {
                                          barWidth
                                        }),
                                        backgroundColor: barColor,
                                        height: '9px',
                                        transition: 'width 5s linear',
                                    }}
                                />
                            </div>
                        </td>
                        <td>
                          {pendingActions[item.assignedstate_id] || sentActions[item.assignedstate_id] ? (
                              <div className="progress-container"  
                                title={sentActions[item.assignedstate_id] ? 'Served' : 'Cancel'} 
                                onClick={() => 
                                  sentActions[item.assignedstate_id] ? 
                                  removeAction(item.assignedstate_id) :
                                  cancelCompleteAction(item.assignedstate_id)
                                }
                               >
                                {sentActions[item.assignedstate_id] ? (
                                  <b style={{fontSize: "20px", color: 'darkRed'}}>{"\u2716"}</b>
                                ) : (
                                  <>
                                    <svg className="progress-circle" width="24" height="24" viewBox="0 0 36 36">
                                        <circle cx="18" cy="18" r="16"></circle>
                                    </svg>
                                    <span className="progress-x">{"\u2716"}</span> 
                                  </>
                                )}
                              </div>
                          ) : (
                              <div style={{cursor: "pointer", color: "darkGreen"}} title={tr('Ready')}
                                  onClick={() => handleCompleteAction(item.assignedstate_id)}>
                                <b style={{fontSize: "20px"}}>{"\u2714"}</b>
                              </div>
                          )}
                        </td>
                        
                        {/* NEW: Siders column */}
                        <td style={{ textAlign: 'center' }}>
                          {['sitting', 'preparing'].includes(String(item.state_name || '').toLowerCase()) && (
                            <span
                              title={tr('Assign / edit siders')}
                              style={{ cursor: 'pointer', fontSize: 'x-large' }}
                              onClick={() => openAssignMeatsForReservationId(item.reservation_id)}
                            >
                             {bbqIcon()}
                            </span>
                          )}
                        </td>
                        <td>{details[item.reservation_id]?.tables || "No tables"}</td>
                        <td>{details[item.reservation_id]?.client?.lastname || ""} {details[item.reservation_id]?.client?.firstname || ""}</td>
                      </tr>
                    );
                  })}
              </tbody>
          </table>                
        </div>
      )}
      {/* --- Modal: TableSelector for Add Siders --- */}
      {showSidersSelector && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
          onClick={closeSidersSelector}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 12,
              padding: 16,
              maxWidth: isTablet ? '95%' : '80%',
              width: '900px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
              }}
            >
              <h3 style={{ margin: 0 }}>{tr('Select table (active reservations)')}</h3>
              <button
                onClick={closeSidersSelector}
                style={{
                  border: 'none',
                  background: 'transparent',
                  fontSize: 18,
                  cursor: 'pointer',
                }}
              >{tr('✕')}</button>
            </div>

            <TableSelector
              reservationTime={selectorTime}
              selectedAssignedTables={[]}
              onTablesSelected={() => {}}
              areaFilter={null}
              tableFilter={(t) =>
                t.reserved_id &&
                activeReservationIds.includes(Number(t.reserved_id))
              }
              onAreaTabChange={() => {}}
              service_view
              people={0}
              activeReservationId={null}
              onTableClick={handleTablePickedForSiders}
            />

            <div
              style={{
                textAlign: 'right',
                marginTop: 8,
                fontSize: 12,
                color: '#666',
              }}
            >{tr('Click a table to assign / edit siders for its reservation.')}</div>
          </div>
        </div>
      )}
      {/* --- Modal: AssignMeats for selected reservation --- */}
      {assignMeatsReservation && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2100,
          }}
          onClick={handleCloseAssignMeats}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 12,
              padding: 10,
              maxWidth: '96%',
              width: isTablet ? '95%' : '1100px',
              maxHeight: '95vh',
              overflow: 'auto',
              boxShadow: '0 8px 30px rgba(0,0,0,0.35)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <AssignMeats
              reservation={assignMeatsReservation}
              onCloseModal={handleCloseAssignMeats}
              onBeforeSave={null}
            />
          </div>
        </div>
      )}
    </div>
  </>
  );
};

export default KitchenView;
